/**
 * Booking-form helpers — small presentational pieces used inside
 * Passenger.jsx (iter-77 decomposition slice 1).
 *
 * Strict isolation contract (matches Driver.jsx playbook):
 *   • Pure presentational. ZERO internal state. ZERO effects.
 *   • Every behavioural decision flows from the parent via props.
 *   • All existing data-testids preserved verbatim.
 *   • Public exports byte-equivalent to the inline JSX they replaced.
 *
 * Rollback: delete this file, paste the 4 const blocks back into
 * Passenger.jsx in their original positions, remove the import
 * line, remove `export ` from PRESET_LOCATIONS. No coupling to
 * any other Passenger component.
 */
import React from "react";
import { Label } from "../ui/label";
import { Plus, X, MapPin } from "lucide-react";

import { PlaceAutocomplete } from "../PlaceAutocomplete";


/**
 * LocationSelect — combo of label, autocomplete, and preset-chip row.
 * Used twice in the booking form (PICKUP + DROP-OFF). The colour pip
 * + the label distinguish them.
 *
 *   value      — current selection ({name, lat, lng})
 *   onChange   — parent's setter
 *   color      — pip colour (#10B981 for pickup, #EF4444 for dropoff)
 *   testId     — used to compose child data-testids
 *   onPickOnMap— optional; when present, shows "PICK ON MAP →" link
 */
export const LocationSelect = ({ label, value, onChange, color, testId, onPickOnMap }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <Label className="label-mini text-muted-foreground flex items-center gap-2">
        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
        {label}
      </Label>
      {onPickOnMap && (
        <button type="button" onClick={onPickOnMap}
          className="label-mini text-[#002FA7] hover:underline flex items-center gap-1"
          data-testid={`${testId}-pick-on-map`}>
          PICK ON MAP →
        </button>
      )}
    </div>
    <div className="border border-border bg-white">
      <PlaceAutocomplete
        value={value}
        onChange={onChange}
        placeholder={`Where ${label === "PICKUP" ? "from" : "to"}?`}
        testId={`${testId}-name-input`}
        requireSelection
      />
    </div>
  </div>
);


/**
 * Stat — single label-over-value cell used in the fare quote header.
 * The `accent` prop is an optional CSS colour applied only to the value.
 */
export const Stat = ({ label, value, accent }) => (
  <div>
    <div className="label-mini text-muted-foreground">{label}</div>
    <div className="font-heading font-black text-lg mt-0.5" style={accent ? { color: accent } : {}}>{value}</div>
  </div>
);


/**
 * StopsEditor — multi-stop journey waypoint editor (#8).
 *
 * Lets a passenger add up to `max` real geocoded intermediate stops
 * (address autocomplete each) routed between pickup and drop-off. Each
 * stop is a {name, lat, lng} object — the same shape PlaceAutocomplete
 * emits for pickup/drop-off. Add/remove only (no reorder in Phase 1).
 * The parent owns the `stops` array; this component is pure presentation.
 *
 *   stops    — array of {name, lat, lng} | null entries
 *   onChange — parent's setter, receives the next array
 *   max      — cap on number of stops (default 3)
 */
export const StopsEditor = ({ stops, onChange, max = 3 }) => {
  const setStop = (i, val) => {
    const next = stops.slice();
    next[i] = val;
    onChange(next);
  };
  const addStop = () => {
    if (stops.length < max) onChange([...stops, null]);
  };
  const removeStop = (i) => onChange(stops.filter((_, idx) => idx !== i));

  return (
    <div className="p-3 space-y-2.5" data-testid="stops-editor">
      <div className="flex items-center gap-3">
        <div className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
          <MapPin className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold">Extra stops</div>
          <div className="text-xs text-muted-foreground">Add up to {max} stops along the way · +£1 each</div>
        </div>
      </div>

      {stops.map((s, i) => (
        <div key={i} className="flex items-center gap-2" data-testid={`stop-row-${i}`}>
          <span className="label-mini text-muted-foreground w-11 flex-shrink-0">STOP {i + 1}</span>
          <div className="flex-1 min-w-0 border border-border bg-white">
            <PlaceAutocomplete
              value={s}
              onChange={(v) => setStop(i, v)}
              placeholder="Add a stop address"
              testId={`stop-${i}-input`}
            />
          </div>
          <button
            type="button"
            onClick={() => removeStop(i)}
            className="w-8 h-8 border border-border hover:bg-secondary flex items-center justify-center flex-shrink-0"
            data-testid={`stop-${i}-remove`}
            aria-label={`Remove stop ${i + 1}`}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}

      {stops.length < max && (
        <button
          type="button"
          onClick={addStop}
          className="w-full py-2 border border-dashed border-border text-sm font-semibold hover:bg-secondary/40 flex items-center justify-center gap-2 transition-colors"
          data-testid="add-stop-btn"
        >
          <Plus className="w-4 h-4" /> Add stop
        </button>
      )}
    </div>
  );
};


/**
 * ToggleRow — generic icon + label + sublabel + iOS-style switch row.
 * Used for the extras toggles (luggage, baby seat, pet, etc.). The
 * parent provides the `icon` (any lucide-react component instance),
 * `label`, `sub`, and the controlled `checked`/`onChange` pair.
 */
export const ToggleRow = ({ icon, label, sub, checked, onChange, testId }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className="w-full p-3 flex items-center gap-3 text-left hover:bg-secondary/30 transition-colors"
    data-testid={testId}
  >
    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${checked ? "bg-[#0A0A0A] text-white" : "bg-secondary"}`}>
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-sm font-semibold">{label}</div>
      <div className="text-xs text-muted-foreground">{sub}</div>
    </div>
    <div className={`w-10 h-5 rounded-full transition-colors relative flex-shrink-0 ${checked ? "bg-[#10B981]" : "bg-muted"}`}>
      <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
    </div>
  </button>
);
